/**
 * EVL-4 / EVL-5 — `RequirementLineageInspector`.
 *
 * Dedicated modal for the Coverage Matrix chip-strip "show evidence
 * chain" affordance. Wraps the universal `EvidenceLineageInspector`
 * with one additional channel — a "Generation will use" tab — and
 * pre-populates the Sources tab from the consolidated
 * `RequirementLineage` (EVL-1) so the modal walks all five tiers
 * (authored × SED slots × DCT × DAM × site research × prior
 * reports), not just the legacy doc-match-against-checklist shape.
 *
 * Why a wrapper and not a mutation of `EvidenceLineageInspector`:
 *   - The existing inspector is shared across SED variable / section /
 *     report-claim surfaces and has well-pinned tests. Mutating
 *     `resolveChecklistRow` inside `useEvidenceLineage.ts` would touch
 *     every consumer.
 *   - This wrapper composes — projecting `RequirementLineage` →
 *     `EvidenceLineage` for the universal modal AND rendering the
 *     new tab. Stage-2 of EVL (deferred follow-ups in the prompt
 *     pack) can promote this projection into the universal inspector
 *     when every surface is ready.
 *
 * Discoverability — the prompt pack's user quote:
 *   *"How do I know what is being sent to the SED, reports etc with
 *    this section? What is populating it DAM, User Docs, API, AI
 *    Research?"*
 *
 * The Generation tab answers that directly: a per-block enumeration
 * of the prompt body that the report-generation LLM call would
 * receive for this requirement.
 */
import React from 'react';
import { X, Info, ListTree, FileText, ChevronRight, ChevronDown, ClipboardCopy, Eye } from 'lucide-react';
import type { RequirementLineage, PromptSlice } from '../../../lib/evidenceLineage/requirementLineage';
import { resolvePromptInclusion, estimateTokens } from '../../../lib/evidenceLineage/requirementLineage';
import type { DatacrateModel, ReportType } from '../../../types/datacrateModel';
import { loadCurrentDam as defaultLoadCurrentDam } from '../../../services/dam/damStorage';
import { isDamLiveModeEnabled } from '../../../services/dam/damLiveMode';
import { ResponsiveTabStrip } from '../../ui/responsive-tab-strip';

export type RequirementLineageInspectorTab = 'sources' | 'generation' | 'verbatim';

/**
 * EVL-FOLLOWUP-HYDRATE-DAM — hydration status for the DAM channel.
 *
 *   - `'preloaded'` — the parent passed a DAM through the lineage
 *                     projection; no hydration needed.
 *   - `'loading'`   — the inspector is fetching the DAM for the
 *                     lineage's `reportType` from Firestore; the
 *                     subtitle reads "Loading DAM…" rather than
 *                     "No DAM loaded." (the misleading legacy copy).
 *   - `'hydrated'`  — fetch resolved with a DAM; render the live info.
 *   - `'missing'`   — fetch resolved to null (DAM genuinely doesn't
 *                     exist for this reportType, or wasn't built yet).
 *                     The ONLY state where we surface "No DAM
 *                     available" copy to the user.
 *   - `'error'`     — fetch threw; render an error hint rather than
 *                     conflating with the "missing" branch (the
 *                     operator response is different — check console
 *                     vs. rebuild the DAM).
 */
type DamHydrationStatus = 'preloaded' | 'loading' | 'hydrated' | 'missing' | 'error';

export interface RequirementLineageInspectorProps {
  open: boolean;
  onClose: () => void;
  /** Pre-resolved lineage from `getRequirementLineage`. */
  lineage: RequirementLineage;
  /** Initial tab — defaults to `'sources'`. */
  initialTab?: RequirementLineageInspectorTab;
  /**
   * EVL-FOLLOWUP-HYDRATE-DAM — optional injected DAM loader. Defaults
   * to `damStorage.loadCurrentDam` so production callers don't need
   * to thread anything through. Tests inject a stub to exercise each
   * hydration branch without standing up Firestore.
   *
   * The loader returns `null` when the DAM doc is genuinely absent in
   * Firestore (vs. throwing, which we treat as `'error'`).
   */
  loadDam?: (reportType: ReportType) => Promise<DatacrateModel | null>;
  /** EVL-5-RESOLVED — optional verbatim prompt slices for the
   *  "What the LLM saw" tab. When omitted, the tab is still rendered
   *  but renders a placeholder explaining how to enable it (the host
   *  hasn't supplied a sedText or `slices` prop yet). */
  slices?: ReadonlyArray<PromptSlice>;
  /** EVL-5-RESOLVED — when `slices` is omitted and `sedText` is
   *  provided, the inspector resolves them on the fly via
   *  `resolvePromptInclusion`. This keeps the host integration
   *  one-line: pass `sedText={sedToText(sed)}` (memoise on the SED
   *  version) and the verbatim slices materialize automatically. */
  sedText?: string;
}

export const RequirementLineageInspector: React.FC<RequirementLineageInspectorProps> = ({
  open,
  onClose,
  lineage,
  initialTab = 'sources',
  loadDam,
  slices,
  sedText,
}) => {
  const [tab, setTab] = React.useState<RequirementLineageInspectorTab>(initialTab);

  // EVL-FOLLOWUP-HYDRATE-DAM — if the parent didn't thread a DAM
  // through `getRequirementLineage`, hydrate it here so the modal can
  // distinguish "still loading" from "genuinely missing". The previous
  // implementation read `lineage.sources.dam` directly and rendered
  // "No DAM loaded." in BOTH cases, which the user reported as a
  // false negative (the DAM IS loaded — just not into THIS modal's
  // lineage projection).
  const preloadedDam = lineage.sources.dam;
  const [hydratedDam, setHydratedDam] = React.useState<DatacrateModel | null>(null);
  const [hydrationStatus, setHydrationStatus] = React.useState<DamHydrationStatus>(
    preloadedDam ? 'preloaded' : 'loading',
  );

  React.useEffect(() => {
    // No work needed when (a) the modal isn't open, or (b) the parent
    // already supplied the DAM via the lineage projection.
    if (!open) return;
    if (preloadedDam) {
      setHydrationStatus('preloaded');
      return;
    }
    let cancelled = false;
    setHydrationStatus('loading');
    setHydratedDam(null);
    const loader = loadDam ?? defaultLoadCurrentDam;
    (async () => {
      try {
        const dam = await loader(lineage.reportType as ReportType);
        if (cancelled) return;
        if (dam) {
          setHydratedDam(dam);
          setHydrationStatus('hydrated');
        } else {
          setHydrationStatus('missing');
        }
      } catch (e) {
        if (!cancelled) {
          setHydrationStatus('error');
          // !cancelled gate (the surrounding if) enforces the project
          // console-discipline rule for async-effect catch blocks.
          // eslint-disable-next-line no-console
          console.warn('[RequirementLineageInspector] DAM hydration failed', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-hydrate when the lineage's reportType changes or the modal
    // re-opens against a different lineage.
  }, [open, preloadedDam, lineage.reportType, loadDam]);

  // EVL-5-RESOLVED — materialize slices on demand. Memoised against
  // the lineage identity + sedText so re-renders don't re-walk a 30 KB
  // string for every parent state change. When `slices` is explicitly
  // supplied, the caller owns memoisation.
  const resolvedSlices: ReadonlyArray<PromptSlice> = React.useMemo(() => {
    if (slices) return slices;
    return resolvePromptInclusion(
      lineage.requirementId,
      lineage.promptInclusion.sedTextSectionIds,
      lineage.sources.authored,
      lineage.sources.dam,
      lineage.sources.sedSlots,
      sedText,
    );
  }, [slices, sedText, lineage]);

  if (!open) return null;
  const totalSources =
    lineage.sources.authored.length +
    lineage.sources.sedSlots.length +
    lineage.sources.dctMatches.length +
    (lineage.sources.dam?.relevantReferences.length ?? 0) +
    (lineage.sources.siteResearch ? 1 : 0) +
    lineage.sources.priorReports.length;
  const totalTokens = resolvedSlices.reduce((acc, s) => acc + s.estimatedTokens, 0);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Requirement lineage inspector"
      data-testid="requirement-lineage-inspector"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[680px] max-h-[85vh] flex flex-col bg-surface-1 border border-border rounded shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="min-w-0">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-text-secondary">
              Evidence chain
            </h2>
            <div className="text-[13px] font-medium text-text-primary truncate">
              {lineage.requirementLabel}
              <span className="text-text-muted text-[10px] ml-2 font-normal uppercase tracking-widest">
                · {lineage.status}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="p-1 text-text-secondary hover:text-text-primary"
            onClick={onClose}
            aria-label="Close inspector"
            data-testid="requirement-lineage-inspector-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <ResponsiveTabStrip
          ariaLabel="Evidence chain sections"
          testId="requirement-lineage-inspector-tabs"
          navClassName="bg-surface-2 border-b-0"
          tabs={[
            {
              id: 'sources',
              label: 'Sources',
              icon: Info,
              count: totalSources,
              testId: 'requirement-lineage-inspector-tab-sources',
            },
            {
              id: 'generation',
              label: 'Generation will use',
              shortLabel: 'Generation',
              icon: ListTree,
              testId: 'requirement-lineage-inspector-tab-generation',
            },
            {
              id: 'verbatim',
              label: 'What the LLM saw',
              shortLabel: 'LLM saw',
              icon: Eye,
              count: resolvedSlices.length,
              testId: 'requirement-lineage-inspector-tab-verbatim',
            },
          ]}
          activeId={tab}
          onSelect={id => setTab(id as typeof tab)}
        />

        <div
          className="flex-1 overflow-y-auto px-4 py-4 text-[12px]"
          data-testid="requirement-lineage-inspector-body"
        >
          {tab === 'sources' && <SourcesTab lineage={lineage} />}
          {tab === 'generation' && (
            <GenerationTab
              lineage={lineage}
              hydrationStatus={hydrationStatus}
              hydratedDam={hydratedDam}
            />
          )}
          {tab === 'verbatim' && (
            <VerbatimTab
              slices={resolvedSlices}
              hasSedText={typeof sedText === 'string' && sedText.length > 0}
              totalTokens={totalTokens}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Sources tab ─────────────────────────────────────────────────── */

const SourcesTab: React.FC<{ lineage: RequirementLineage }> = ({ lineage }) => {
  const { sources } = lineage;
  const isEmpty =
    sources.authored.length === 0 &&
    sources.sedSlots.length === 0 &&
    sources.dctMatches.length === 0 &&
    !sources.dam &&
    !sources.siteResearch &&
    sources.priorReports.length === 0;
  if (isEmpty) {
    return (
      <div className="text-text-muted text-[11px]" data-testid="requirement-lineage-inspector-sources-empty">
        No provenance recorded for this requirement. Upload a document or
        click <span className="font-bold">Author</span> to capture what
        you know.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {sources.authored.length > 0 && (
        <SourceSection title="Authored evidence" icon="✍" testid="authored">
          {sources.authored.map(c => (
            <li key={c.id} className="border-l-2 border-info/40 pl-2 py-0.5">
              <div className="text-text-primary text-[11px]">{c.claim || c.sourceSpan}</div>
              <div className="text-[10px] text-text-muted mt-0.5">
                {c.authoredKind ?? 'prose'} · confidence {Math.round((c.extractorConfidence ?? 0) * 100)}%
              </div>
            </li>
          ))}
        </SourceSection>
      )}
      {sources.sedSlots.length > 0 && (
        <SourceSection title="SED slots (providers + documents)" icon="🔌" testid="slots">
          {sources.sedSlots.map(s => (
            <li key={`${s.sectionId}-${s.providerKey ?? ''}`} className="border-l-2 border-primary/40 pl-2 py-0.5">
              <div className="text-text-primary text-[11px]">
                {s.fieldLabel ?? s.sectionId}
                <span className="text-text-muted text-[10px] ml-1.5">· {s.providerLabel ?? s.providerKey ?? 'Provider'}</span>
              </div>
              <div className="text-[10px] text-text-muted mt-0.5">
                {s.provenance}
                {' · '}
                confidence {Math.round((s.confidence ?? 0) * 100)}%
                {s.docNames.length > 0 && (
                  <>
                    {' · '}
                    {s.docNames.length} doc{s.docNames.length === 1 ? '' : 's'}: {s.docNames.join(', ')}
                  </>
                )}
                {s.pageNumber && <> · page {s.pageNumber}</>}
              </div>
              {s.evidenceText && (
                <div className="text-text-secondary text-[10px] italic mt-1">“{s.evidenceText}”</div>
              )}
            </li>
          ))}
        </SourceSection>
      )}
      {sources.dam && sources.dam.relevantReferences.length > 0 && (
        <SourceSection
          title={`Datacrate Model (${sources.dam.isLive ? 'live · ' : 'static · '}${sources.dam.version})`}
          icon="🤖"
          testid="dam"
        >
          {sources.dam.relevantReferences.map((ref, i) => (
            <li key={`${ref.field}-${i}`} className="border-l-2 border-emerald-500/40 pl-2 py-0.5">
              <div className="text-text-primary text-[11px]">{ref.source}</div>
              <div className="text-[10px] text-text-muted mt-0.5">
                {ref.field} · captured {ref.retrievedAt}
              </div>
            </li>
          ))}
        </SourceSection>
      )}
      {sources.siteResearch && (
        <SourceSection title="AI site research" icon="🔍" testid="research">
          <li className="border-l-2 border-amber-500/40 pl-2 py-0.5">
            <div className="text-text-primary text-[11px]">{sources.siteResearch.source}</div>
            <div className="text-[10px] text-text-muted mt-0.5">
              Retrieved {sources.siteResearch.retrievedAt} ·
              {' '}
              contributes to {sources.siteResearch.contributesTo.join(', ')}
            </div>
          </li>
        </SourceSection>
      )}
      {sources.priorReports.length > 0 && (
        <SourceSection title="Prior reports on this site" icon="📊" testid="prior">
          {sources.priorReports.map(r => (
            <li key={`${r.reportId}-${r.sedFieldPath}`} className="border-l-2 border-violet-500/40 pl-2 py-0.5">
              <div className="text-text-primary text-[11px]">{r.reportType} · {r.sedFieldPath}</div>
              <div className="text-[10px] text-text-muted mt-0.5">
                confidence {Math.round(r.confidence * 100)}%
              </div>
            </li>
          ))}
        </SourceSection>
      )}
      {sources.dctMatches.length > 0 && (
        <SourceSection title="DCT matched documents" icon="📄" testid="dct">
          {sources.dctMatches.map(m => (
            <li key={m.docId} className="border-l-2 border-text-muted/40 pl-2 py-0.5">
              <div className="text-text-primary text-[11px]">
                {m.docId}
                <span className="text-text-muted text-[10px] ml-1.5">
                  · {m.role} · score {Math.round(m.score * 100)}%
                </span>
              </div>
              {m.rationale && (
                <div className="text-text-secondary text-[10px] italic mt-0.5">{m.rationale}</div>
              )}
            </li>
          ))}
        </SourceSection>
      )}
    </div>
  );
};

const SourceSection: React.FC<{
  title: string;
  icon: string;
  testid: string;
  children: React.ReactNode;
}> = ({ title, icon, testid, children }) => (
  <section data-testid={`requirement-lineage-inspector-section-${testid}`}>
    <div className="type-section text-text-secondary mb-1.5 flex items-center gap-1.5">
      <span className="inline-flex items-center justify-center w-4 h-4 border border-border-subtle bg-surface-2 text-[10px] font-mono">
        {icon}
      </span>
      {title}
    </div>
    <ul className="space-y-1.5 list-none">{children}</ul>
  </section>
);

/* ── Generation tab ──────────────────────────────────────────────── */

/**
 * EVL-FOLLOWUP-HYDRATE-DAM — pick the user-facing subtitle for the
 * DAM section. The legacy logic was "if lineage.sources.dam render
 * version; else render 'No DAM loaded.'", which silently shipped
 * "No DAM loaded." in two unrelated cases:
 *   1. The parent never threaded a DAM through the lineage projection
 *      (current SiteLandingPage behavior — `dam` is undefined). The
 *      DAM ALMOST CERTAINLY exists in Firestore for the live reportTypes;
 *      saying "No DAM loaded" was a false negative.
 *   2. The DAM genuinely does not exist (rare — only when the type's
 *      DAM has never been built or was deleted).
 *
 * The hydration effect (above) discriminates the two. This helper
 * maps the hydration status to a precise, actionable subtitle.
 */
function damSubtitle(
  lineage: RequirementLineage,
  hydrationStatus: DamHydrationStatus,
  hydratedDam: DatacrateModel | null,
  damRefCount: number,
): string {
  // Preloaded — parent supplied the DAM through getRequirementLineage.
  if (lineage.sources.dam) {
    return `Live · ${lineage.sources.dam.version} · ${damRefCount} ref${damRefCount === 1 ? '' : 's'}`;
  }
  if (hydrationStatus === 'loading') return 'Loading DAM…';
  if (hydrationStatus === 'error') return 'DAM load failed (check console).';
  if (hydrationStatus === 'hydrated' && hydratedDam) {
    const live = isDamLiveModeEnabled(lineage.reportType as ReportType);
    const refs = hydratedDam.references?.length ?? 0;
    const versionLabel =
      typeof hydratedDam.version === 'number'
        ? `v${String(hydratedDam.version).padStart(4, '0')}`
        : String(hydratedDam.version ?? 'v0001');
    return `${live ? 'Live' : 'Static'} · ${versionLabel} · ${refs} ref${refs === 1 ? '' : 's'} available`;
  }
  // Only emit the "no DAM" copy when hydration COMPLETED and the doc
  // genuinely wasn't there. The legacy string was misleading; this
  // one carries the actionable rebuild command.
  return `No DAM available for ${lineage.reportType} — build with: npm run build:dam -- --reportType=${lineage.reportType}`;
}

const GenerationTab: React.FC<{
  lineage: RequirementLineage;
  hydrationStatus: DamHydrationStatus;
  hydratedDam: DatacrateModel | null;
}> = ({ lineage, hydrationStatus, hydratedDam }) => {
  const inc = lineage.promptInclusion;

  // EVL-5 follow-up — verbatim resolvers per Generation-section row.
  //
  // The pre-fix carets were a decorative `<ChevronRight>` icon on every
  // row that didn't expand — the user observed they were inert. The
  // user's underlying ask is "what verbatim text reaches the LLM for
  // this row?", which the lineage *already* knows but used to bury
  // inside the Sources tab. These resolvers project a per-row "detail"
  // payload from the same `lineage` object so a click on the caret
  // expands the row to the actual evidenceText / claim verbatim /
  // provider-data / DAM source / page reference — no new IO.
  //
  // Falls back to `null` when no verbatim is available (e.g. an empty
  // input-layer field), in which case the caret is rendered inert (no
  // click handler, no toggle state). This avoids advertising
  // expandability for rows that have nothing to expand to.

  const sedSlotsByKey = React.useMemo(() => {
    const m = new Map<string, typeof lineage.sources.sedSlots[number]>();
    for (const s of lineage.sources.sedSlots) m.set(s.sectionId, s);
    return m;
  }, [lineage.sources.sedSlots]);

  const claimsById = React.useMemo(() => {
    const m = new Map<string, typeof lineage.sources.authored[number]>();
    for (const c of lineage.sources.authored) m.set(c.id, c);
    return m;
  }, [lineage.sources.authored]);

  return (
    <div className="space-y-4">
      <div
        className="text-[10px] text-text-secondary border border-border-subtle bg-surface-2 px-2 py-1.5"
        data-testid="requirement-lineage-inspector-generation-blurb"
      >
        These are the prompt-body blocks that the report-generation LLM
        would see for <span className="font-bold">{lineage.requirementLabel}</span>.
        A reference being listed here means it&apos;s <em>available</em> to the
        model — not that the model will quote it. Citation accuracy is
        tracked separately by the Citation Quality dashboard.
      </div>

      <GenerationSection
        title="Resolved User Config (inputLayersBlock)"
        subtitle="IC4 — projectIntent + typedPrefs + siteContext fields visible to this requirement."
        testid="input-layers"
      >
        {inc.inputLayersFields.length === 0 ? (
          <Empty>No requirement-specific input fields.</Empty>
        ) : (
          inc.inputLayersFields.map(f => <Row key={f} label={f} />)
        )}
      </GenerationSection>

      <GenerationSection
        title="SED text blocks (sedToText)"
        subtitle="SED sections serialized into the prompt body via sedToText."
        testid="sed-text"
      >
        {inc.sedTextSectionIds.length === 0 ? (
          <Empty>No SED text blocks reach the LLM for this requirement.</Empty>
        ) : (
          inc.sedTextSectionIds.map(s => {
            const slot = sedSlotsByKey.get(s);
            const detail =
              slot && (slot.evidenceText || slot.providerData != null)
                ? renderSlotDetail(slot)
                : null;
            return (
              <Row
                key={s}
                label={<><FileText className="w-3 h-3 inline mr-1" />{s}</>}
                detail={detail}
                testid={`sed-text-${s}`}
              />
            );
          })
        )}
      </GenerationSection>

      <GenerationSection
        title="Authored claims (siteContextBlock)"
        subtitle="SC8 — user-authored evidence serialized."
        testid="site-context"
      >
        {inc.siteContextClaimIds.length === 0 ? (
          <Empty>No authored claims for this requirement.</Empty>
        ) : (
          inc.siteContextClaimIds.map(id => {
            const claim = claimsById.get(id);
            const detail = claim
              ? (
                <div className="space-y-1">
                  <div className="text-[11px] text-text-primary italic">
                    “{claim.claim || claim.sourceSpan}”
                  </div>
                  <div className="text-[10px] text-text-muted">
                    {claim.authoredKind ?? 'prose'} · confidence{' '}
                    {Math.round((claim.extractorConfidence ?? 0) * 100)}%
                  </div>
                </div>
              )
              : null;
            return <Row key={id} label={id} detail={detail} testid={`site-context-${id}`} />;
          })
        )}
      </GenerationSection>

      <GenerationSection
        title="DAM references"
        subtitle={damSubtitle(lineage, hydrationStatus, hydratedDam, inc.damReferenceRefIds.length)}
        testid="dam"
      >
        {inc.damReferenceRefIds.length === 0 ? (
          // Show a precise placeholder so the operator can tell whether
          // (a) the modal is still hydrating, (b) the DAM is hydrated
          // but this requirement has no DAM refs (a real "no relevant
          // refs" outcome — different from "DAM not loaded"), or
          // (c) the DAM is missing.
          hydrationStatus === 'loading' && !lineage.sources.dam ? (
            <Empty>Loading DAM references…</Empty>
          ) : (
            <Empty>No DAM references for this requirement.</Empty>
          )
        ) : (
          inc.damReferenceRefIds.map(n => {
            const ref = lineage.sources.dam?.relevantReferences[n - 1];
            const detail = ref
              ? (
                <div className="text-[10px] text-text-muted space-y-0.5">
                  <div><span className="font-semibold text-text-secondary">Field:</span> {ref.field}</div>
                  <div><span className="font-semibold text-text-secondary">Retrieved:</span> {ref.retrievedAt}</div>
                </div>
              )
              : null;
            return (
              <Row
                key={n}
                label={`[Ref ${n}] ${ref?.source ?? '(unknown DAM ref)'}`}
                detail={detail}
                testid={`dam-${n}`}
              />
            );
          })
        )}
      </GenerationSection>

      <GenerationSection
        title="Provider references"
        subtitle="[Ref N] entries resolving to live API providers (WRI, FEMA, USGS, NREL, …)."
        testid="provider"
      >
        {inc.providerReferenceRefIds.length === 0 ? (
          <Empty>No provider references for this requirement.</Empty>
        ) : (
          inc.providerReferenceRefIds.map(n => {
            const slot = lineage.sources.sedSlots.filter(
              s => s.provenance === 'provider' || s.provenance === 'provider+doc',
            )[n - 1];
            const detail = slot ? renderSlotDetail(slot) : null;
            return (
              <Row
                key={n}
                label={`[Ref ${n}] ${slot?.providerLabel ?? slot?.providerKey ?? '(unknown provider)'}`}
                detail={detail}
                testid={`provider-${n}`}
              />
            );
          })
        )}
      </GenerationSection>

      <GenerationSection
        title="Document references"
        subtitle="[Ref N] entries resolving to user-uploaded documents."
        testid="doc"
      >
        {inc.docReferenceRefIds.length === 0 ? (
          <Empty>No document references for this requirement.</Empty>
        ) : (
          inc.docReferenceRefIds.map((n, i) => {
            // A doc ref maps onto whichever SED slot contributed the
            // doc; we walk slots in order and grab the i-th doc name
            // (the same iteration the prompt uses).
            const docNames = lineage.sources.sedSlots.flatMap(s => s.docNames);
            const docName = docNames[i];
            // Find the slot that supplied this doc to populate detail.
            const slot = lineage.sources.sedSlots.find(s => s.docNames.includes(docName ?? ''));
            const detail = slot ? renderSlotDetail(slot) : null;
            return (
              <Row
                key={n}
                label={`[Ref ${n}] ${docName ?? '(unknown document)'}`}
                detail={detail}
                testid={`doc-${n}`}
              />
            );
          })
        )}
      </GenerationSection>
    </div>
  );
};

/**
 * Render the verbatim detail block for a SED slot. Surfaces the ≤200-char
 * evidenceText (REF-2) when present plus a small key→value list for
 * structured provider data, page number, and confidence.
 */
function renderSlotDetail(slot: import('../../../lib/evidenceLineage/requirementLineage').SedSlotLineage): React.ReactNode {
  // We render even when only metadata is available — the user's ask was
  // "tell me MORE", and confidence/provenance carries meaning even
  // without a verbatim quote.
  const providerData = slot.providerData && typeof slot.providerData === 'object'
    ? Object.entries(slot.providerData as Record<string, unknown>)
        .filter(([, v]) => v != null && v !== '' && typeof v !== 'object')
    : [];
  return (
    <div className="space-y-1">
      {slot.evidenceText && (
        <div className="text-[11px] text-text-primary italic">“{slot.evidenceText}”</div>
      )}
      <div className="text-[10px] text-text-muted space-y-0.5">
        <div>
          <span className="font-semibold text-text-secondary">Provenance:</span> {slot.provenance}
          {' · '}
          <span className="font-semibold text-text-secondary">Confidence:</span>{' '}
          {Math.round((slot.confidence ?? 0) * 100)}%
        </div>
        {slot.fieldLabel && (
          <div><span className="font-semibold text-text-secondary">Field:</span> {slot.fieldLabel}</div>
        )}
        {slot.pageNumber != null && (
          <div><span className="font-semibold text-text-secondary">Page:</span> {slot.pageNumber}</div>
        )}
        {providerData.length > 0 && (
          <div className="pt-1">
            {providerData.map(([k, v]) => (
              <div key={k}>
                <span className="font-semibold text-text-secondary">{k}:</span> {String(v)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const GenerationSection: React.FC<{
  title: string;
  subtitle?: string;
  testid: string;
  children: React.ReactNode;
}> = ({ title, subtitle, testid, children }) => (
  <section data-testid={`requirement-lineage-inspector-generation-${testid}`}>
    <div className="type-section text-text-secondary mb-1.5">
      {title}
    </div>
    {subtitle && (
      <p className="text-[10px] text-text-muted mb-1.5">{subtitle}</p>
    )}
    <ul className="space-y-0.5 list-none">{children}</ul>
  </section>
);

/**
 * EVL-5 follow-up — disclosure-capable row.
 *
 * `label` is the always-visible row text. When `detail` is non-null,
 * the leading caret becomes a toggle button: clicking it expands the
 * row to reveal the verbatim evidenceText / provider data / DAM source
 * meta below. When `detail` is null the caret renders as a decorative,
 * inert icon (matches the pre-fix appearance for rows with nothing to
 * expand — e.g. an empty `inputLayersFields` entry).
 *
 * Why a button-on-the-row instead of a separate "show details" link:
 * the user observed the carets and assumed they were interactive. We
 * meet that affordance directly — the same icon they clicked now
 * actually does something.
 */
const Row: React.FC<{
  label: React.ReactNode;
  detail?: React.ReactNode | null;
  testid?: string;
}> = ({ label, detail, testid }) => {
  const [expanded, setExpanded] = React.useState(false);
  const expandable = detail != null;
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <li className="text-[11px] text-text-primary" data-testid={testid ? `generation-row-${testid}` : undefined}>
      <div className="flex items-start gap-1">
        {expandable ? (
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse verbatim detail' : 'Expand verbatim detail'}
            data-testid={testid ? `generation-row-toggle-${testid}` : undefined}
            className="mt-0.5 shrink-0 text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            <Icon className="w-3 h-3" />
          </button>
        ) : (
          <Icon className="w-3 h-3 text-text-muted mt-0.5 shrink-0" aria-hidden="true" />
        )}
        <span className="flex-1">{label}</span>
      </div>
      {expandable && expanded && (
        <div
          className="ml-4 mt-1 border-l-2 border-border-subtle pl-2 py-1 bg-surface-2/40"
          data-testid={testid ? `generation-row-detail-${testid}` : undefined}
        >
          {detail}
        </div>
      )}
    </li>
  );
};

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="text-[10px] text-text-muted italic">{children}</li>
);

/* ── Verbatim "What the LLM saw" tab (EVL-5-RESOLVED) ────────────── */

const SLICE_TONE: Record<PromptSlice['kind'], string> = {
  'sed-section':  'border-primary/40',
  'input-layers': 'border-info/40',
  'site-context': 'border-info/40',
  'dam-ref':      'border-emerald-500/40',
  'provider-ref': 'border-primary/40',
  'doc-ref':      'border-text-muted/40',
};

const SLICE_ICON: Record<PromptSlice['kind'], string> = {
  'sed-section':  '📄',
  'input-layers': '⚙',
  'site-context': '✍',
  'dam-ref':      '🤖',
  'provider-ref': '🔌',
  'doc-ref':      '📄',
};

/**
 * EVL-5-RESOLVED — renders the verbatim prompt slices in <pre>
 * blocks with per-slice copy-to-clipboard and token estimates. The
 * intent is full transparency: the user can read EXACTLY what
 * reaches the LLM for this requirement, and copy any individual
 * block for offline inspection / sharing.
 *
 * Clipboard semantics — graceful degrade when the browser lacks
 * `navigator.clipboard` (e.g. JSDOM in tests, insecure contexts).
 * The button always renders; the state flashes "Copied!" for 1.2s
 * after a successful write, otherwise leaves the label unchanged
 * and surfaces a tooltip explaining the limitation.
 */
const VerbatimTab: React.FC<{
  slices: ReadonlyArray<PromptSlice>;
  hasSedText: boolean;
  totalTokens: number;
}> = ({ slices, hasSedText, totalTokens }) => {
  if (slices.length === 0) {
    return (
      <div
        className="text-text-muted text-[11px]"
        data-testid="requirement-lineage-inspector-verbatim-empty"
      >
        No prompt slices to render for this requirement.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="requirement-lineage-inspector-verbatim">
      <div
        className="text-[10px] text-text-secondary border border-border-subtle bg-surface-2 px-2 py-1.5"
        data-testid="requirement-lineage-inspector-verbatim-summary"
      >
        {slices.length} slice{slices.length === 1 ? '' : 's'} · ~{totalTokens} tokens
        {' · '}
        chars ÷ 4 heuristic
        {!hasSedText && (
          <>
            {' · '}
            <span className="italic">
              SED-section slices are placeholders until the host threads
              <code className="font-mono mx-1">sedText</code> through.
            </span>
          </>
        )}
        {hasSedText && (
          <>
            {' · '}
            <span className="italic">
              Serialized prompt blocks — the Generation tab shows structured slot values.
            </span>
          </>
        )}
      </div>
      <ul className="space-y-3 list-none">
        {slices.map((s, idx) => (
          <li key={`${s.kind}-${idx}`} data-testid={`requirement-lineage-inspector-verbatim-slice-${s.kind}`}>
            <VerbatimSlice slice={s} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const VerbatimSlice: React.FC<{ slice: PromptSlice }> = ({ slice }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = React.useCallback(() => {
    let cancelled = false;
    try {
      const clip = (typeof navigator !== 'undefined' ? navigator.clipboard : undefined);
      if (clip?.writeText) {
        clip.writeText(slice.text).then(
          () => {
            if (cancelled) return;
            setCopied(true);
            window.setTimeout(() => {
              if (!cancelled) setCopied(false);
            }, 1200);
          },
          (err: unknown) => {
            if (!cancelled) {
              // eslint-disable-next-line no-console
              console.warn('[RequirementLineageInspector] clipboard.writeText rejected', err);
            }
          },
        );
      } else {
        // Insecure context / JSDOM — silently no-op but keep the UI
        // affordance alive so tests still cover the click path.
        setCopied(false);
      }
    } catch (err) {
      if (!cancelled) {
        // eslint-disable-next-line no-console
        console.warn('[RequirementLineageInspector] copy threw', err);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [slice.text]);
  const icon = SLICE_ICON[slice.kind];
  const tone = SLICE_TONE[slice.kind];
  const tokens = Number.isFinite(slice.estimatedTokens) ? slice.estimatedTokens : estimateTokens(slice.text);
  return (
    <div className={`border ${tone} bg-surface-2 px-2 py-1.5`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="type-section text-text-secondary inline-flex items-center gap-1">
          <span aria-hidden="true">{icon}</span>
          {slice.label}
          {slice.refKey && (
            <span className="text-text-muted font-normal normal-case lowercase">
              · {slice.refKey}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="type-body uppercase tracking-widest text-text-secondary hover:text-text-primary inline-flex items-center gap-1 border border-border-subtle hover:border-border px-1.5 py-0.5"
          data-testid={`requirement-lineage-inspector-verbatim-copy-${slice.kind}`}
          title={`Copy this ~${tokens}-token block to the clipboard.`}
          aria-label={`Copy ${slice.label}`}
        >
          <ClipboardCopy className="w-3 h-3" aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
          <span className="text-text-muted font-mono normal-case">· ~{tokens}t</span>
        </button>
      </div>
      <pre
        className="text-[11px] text-text-primary whitespace-pre-wrap break-words bg-surface-1 border border-border-subtle px-2 py-1.5 max-h-[200px] overflow-y-auto font-mono"
        data-testid={`requirement-lineage-inspector-verbatim-text-${slice.kind}`}
      >
        {slice.text}
      </pre>
    </div>
  );
};
